import { ScheduleEditorTranslations, TranslateFunction } from '@locales/generated';
import { useTranslation } from '@shared/core';
import { TextV2, ButtonV2, Box, styled, Flex } from '@withjoy/joykit';
import { Trash } from '@withjoy/joykit/icons';
import React from 'react';

export type ConfirmationDialogMode = 'delete' | 'discard';

type ConfirmationDialogProps = {
  mode: ConfirmationDialogMode | null;
  onDoCancel: () => void;
  setConfirmationType: (type: ConfirmationDialogMode | null) => void;
};

// Styled component for the sheet
const StyledSheet = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 10px;
  width: calc(100% - 20px);
  padding: 48px 24px 24px 24px;
  z-index: 1500;

  border-radius: 24px 24px 0 0;
  background: #fff;

  box-shadow:
    0 -16px 60px -12px rgba(44, 41, 37, 0.25),
    0 -8px 36px -18px rgba(0, 0, 0, 0.3);
`;

const translationMap: Record<NonNullable<ConfirmationDialogProps['mode']>, keyof ScheduleEditorTranslations<TranslateFunction>['confirmationModals']> = {
  ['discard']: 'cancelModal',
  ['delete']: 'deleteModal',
};

export const ConfirmationDialog = (props: ConfirmationDialogProps) => {
  const { mode, onDoCancel, setConfirmationType } = props;
  const { t } = useTranslation('scheduleEditor');

  const trans = t('confirmationModals', translationMap[mode ?? 'delete']);

  return (
    <StyledSheet
      opacity={mode ? 1 : 0}
      userSelect="none"
      pointerEvents={mode ? 'all' : 'none'}
      transition="opacity 0.2s ease"
    >
      <Flex
        gap="24px"
        flexDirection="column"
      >
        <Box>
          <TextV2
            typographyVariant="hed3"
            textAlign="center"
          >
            {trans.title()}
          </TextV2>
          <TextV2
            typographyVariant="hed3"
            textAlign="center"
          >
            {trans.subtitle()}
          </TextV2>
        </Box>
        <Flex
          gap="16px"
          flexDirection="column"
        >
          <ButtonV2
            startIcon={<Trash />}
            intent="destructive"
            shape="rounded"
            variant="solid"
            onClick={onDoCancel}
          >
            {trans.discardCTA()}
          </ButtonV2>
          <ButtonV2
            intent="neutral"
            variant="ghost"
            onClick={() => {
              setConfirmationType(null);
            }}
            marginLeft="8px"
          >
            {trans.goBackCTA()}
          </ButtonV2>
        </Flex>
      </Flex>
    </StyledSheet>
  );
};
